import {
  isSuppressed,
  type DirectoryContact,
  type QuoteClientContact,
} from '@/types/domain'

/* The quote header's contact picker reads its contacts from one of two places:
 * the quote's own route (`GET /quotes/:id/client-contacts`) while the header
 * still names the quote's client, the directory's detail read once somebody
 * picks another client. This turns the directory's rows into the quote route's
 * shape, so the picker and its "Defaults to …" hint read ONE shape whichever
 * source answered.
 *
 * `isDefault` restates quote.firstAddressable — the first contact carrying an
 * address, in the directory's order (primary first) — and the order is kept
 * as served: a re-sort would make the hint name somebody the send would not
 * address. `email` is a suppressible field, so it is read through
 * isSuppressed, never a truthiness test: a Suppressed marker is an OBJECT and
 * would pass one, naming as the default somebody the reader cannot verify. */

function isAddressable(contact: DirectoryContact): boolean {
  return !isSuppressed(contact.email) && (contact.email ?? '').trim() !== ''
}

export function toQuoteClientContacts(
  contacts: readonly DirectoryContact[],
): QuoteClientContact[] {
  const defaultId = contacts.find(isAddressable)?.id
  return contacts.map((contact) => ({
    id: contact.id,
    label: contact.label,
    isPrimary: contact.isPrimary,
    isDefault: contact.id === defaultId,
  }))
}

/* A client created from the picker arrives with its contacts in the create
 * response. With exactly one there is nothing to choose, so it is the contact
 * the offer is for — the same answer an existing client's default gives (N-18).
 * With none, or several, the choice is the salesperson's and stays blank. */
export function soleContactId(client?: { contacts: readonly { id: string }[] }): string {
  return client?.contacts.length === 1 ? (client.contacts[0]?.id ?? '') : ''
}
